import { Directive, ElementRef, Input, AfterViewInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

/**
 * Directive for manipulating elements within a web component's shadow DOM.
 * Supports removing, adding, replacing elements, hiding elements, or removing classes by selector.
 * 
 * @example
 * // Remove an element
 * <rad-chip [appShadowDom]="{ action: 'remove', selector: '.rad-chip__close-button' }"></rad-chip>
 * 
 * // Add HTML content
 * <rad-chip [appShadowDom]="{ action: 'add', selector: '.rad-chip__label', html: '<a>Link</a>' }"></rad-chip>
 * 
 * // Add DOM element (dynamically created component)
 * <rad-accordion [appShadowDom]="{ action: 'add', selector: '.rad-accordion__header', element: myComponentElement }"></rad-accordion>
 * 
 * // Replace content
 * <rad-chip [appShadowDom]="{ action: 'replace', selector: '.rad-chip__label', html: '<a>Link</a>' }"></rad-chip>
 * 
 * // Remove a class
 * <rad-checkbox [appShadowDom]="{ action: 'removeClass', selector: '.rad-checkbox', className: 'rad-checkbox--standalone' }"></rad-checkbox>
 */
@Directive({
  selector: '[appShadowDom]'
})
export class ShadowDomDirective implements AfterViewInit, OnDestroy, OnChanges {
  @Input('appShadowDom') config: ShadowDomConfig | ShadowDomConfig[] = null;
  
  private observer: MutationObserver | null = null;
  private managedElements: Set<HTMLElement> = new Set();
  private removedElements: Map<string, { element: HTMLElement, parent: HTMLElement, nextSibling: Node }> = new Map();

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    this.waitForShadowRoot();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config'] && !changes['config'].firstChange) {
      // Re-apply operations when config changes
      this.applyOperations();
    }
  }

  ngOnDestroy() {
    this.cleanup();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private waitForShadowRoot() {
    const host: HTMLElement = this.el.nativeElement;

    if (host.shadowRoot) {
      this.applyOperations();
    } else {
      // Shadow root might not be ready yet, wait for it
      this.observer = new MutationObserver(() => {
        if (host.shadowRoot) {
          this.observer.disconnect();
          this.applyOperations();
        }
      });

      this.observer.observe(host, {
        childList: true,
        subtree: true
      });

      // Fallback timeout to try applying after a short delay
      setTimeout(() => {
        if (host.shadowRoot && this.managedElements.size === 0) {
          this.applyOperations();
        }
      }, 100);
    }
  }

  private applyOperations() {
    const host: HTMLElement = this.el.nativeElement;

    if (!host.shadowRoot) {
      return;
    }

    // Clean up previous operations first
    this.cleanup();

    // If no config, just cleanup and return
    if (!this.config) {
      return;
    }

    // Handle single config or array of configs
    const configs = Array.isArray(this.config) ? this.config : [this.config];

    configs.forEach(config => {
      switch (config.action) {
        case 'remove':
          this.removeElement(host.shadowRoot, config);
          break;
        case 'add':
          this.addElement(host.shadowRoot, config);
          break;
        case 'replace':
          this.replaceElement(host.shadowRoot, config);
          break;
        case 'hide':
          this.hideElement(host.shadowRoot, config);
          break;
        case 'removeClass':
          this.removeClass(host.shadowRoot, config);
          break;
        default:
          console.warn(`ShadowDomDirective: Unknown action "${config.action}"`);
      }
    });
  }

  private removeElement(shadowRoot: ShadowRoot, config: ShadowDomConfig) {
    const elements = shadowRoot.querySelectorAll(config.selector);
    
    if (elements.length === 0) {
      console.warn(`ShadowDomDirective: No elements found for selector "${config.selector}"`);
      return;
    }

    elements.forEach((element: HTMLElement) => {
      const parent = element.parentElement;
      const nextSibling = element.nextSibling;
      
      // Store reference in case we need to restore
      this.removedElements.set(config.selector, { element, parent, nextSibling });
      
      // Remove the element
      element.remove();
    });
  }

  private hideElement(shadowRoot: ShadowRoot, config: ShadowDomConfig) {
    const elements = shadowRoot.querySelectorAll(config.selector);
    
    if (elements.length === 0) {
      console.warn(`ShadowDomDirective: No elements found for selector "${config.selector}"`);
      return;
    }

    elements.forEach((element: HTMLElement) => {
      element.style.display = 'none';
      this.managedElements.add(element);
    });
  }

  private removeClass(shadowRoot: ShadowRoot, config: ShadowDomConfig) {
    if (!config.className) {
      console.warn(`ShadowDomDirective: No className provided for 'removeClass' action`);
      return;
    }

    const elements = shadowRoot.querySelectorAll(config.selector);
    
    if (elements.length === 0) {
      console.warn(`ShadowDomDirective: No elements found for selector "${config.selector}"`);
      return;
    }

    elements.forEach((element: HTMLElement) => {
      element.classList.remove(config.className);
      this.managedElements.add(element);
    });
  }

  private addElement(shadowRoot: ShadowRoot, config: ShadowDomConfig) {
    if (!config.html && !config.element) {
      console.warn(`ShadowDomDirective: No HTML content or element provided for 'add' action`);
      return;
    }

    const targetElement = shadowRoot.querySelector(config.selector);

    if (!targetElement) {
      console.warn(`ShadowDomDirective: Target selector "${config.selector}" not found in shadow DOM`);
      return;
    }

    let elementToInsert: HTMLElement;

    if (config.element) {
      // Use the provided DOM element directly
      elementToInsert = config.element;
      elementToInsert.classList.add('shadow-dom-injected');
    } else {
      // Create wrapper element for HTML string
      const wrapper = document.createElement('span');
      wrapper.innerHTML = config.html;
      wrapper.className = 'shadow-dom-injected';
      elementToInsert = wrapper;
    }
    
    // Insert based on position
    const position = config.position || 'append';
    switch (position) {
      case 'prepend':
        targetElement.insertBefore(elementToInsert, targetElement.firstChild);
        break;
      case 'append':
        targetElement.appendChild(elementToInsert);
        break;
      case 'before':
        targetElement.parentElement.insertBefore(elementToInsert, targetElement);
        break;
      case 'after':
        targetElement.parentElement.insertBefore(elementToInsert, targetElement.nextSibling);
        break;
    }

    this.managedElements.add(elementToInsert);
    
    // Only attach event listeners for HTML string content (legacy behavior)
    if (config.html) {
      this.attachEventListeners(elementToInsert);
    }
  }

  private replaceElement(shadowRoot: ShadowRoot, config: ShadowDomConfig) {
    if (!config.html && !config.element) {
      console.warn(`ShadowDomDirective: No HTML content or element provided for 'replace' action`);
      return;
    }

    const targetElement = shadowRoot.querySelector(config.selector) as HTMLElement;

    if (!targetElement) {
      console.warn(`ShadowDomDirective: Target selector "${config.selector}" not found in shadow DOM`);
      return;
    }

    // Fix pointer-events for rad-chip: ensure the chip root can receive events
    const chipRoot = targetElement.closest('.rad-chip');
    if (chipRoot) {
      (chipRoot as HTMLElement).style.pointerEvents = 'auto';
    }

    // Ensure the target element can receive pointer events
    targetElement.style.pointerEvents = 'auto';

    if (config.element) {
      // Replace with the provided DOM element
      targetElement.innerHTML = '';
      targetElement.appendChild(config.element);
    } else {
      // Replace the content with HTML string
      targetElement.innerHTML = config.html;
    }
    
    this.managedElements.add(targetElement);
    
    // Only attach event listeners for HTML string content (legacy behavior)
    if (config.html) {
      this.attachEventListeners(targetElement);
    }
  }

  // This logic is incredibly specific to buttons, specifically the button we're adding inside the rad-chips for
  // the resource chooser. Ideally this should either be generalized in the future or we should add the rad
  // team to add this functionality natively to the rad-chip
  private attachEventListeners(element: HTMLElement) {
    // Attach event listeners to buttons
    const buttons = element.querySelectorAll('button');
    
    buttons.forEach((button: HTMLButtonElement) => {
      // Workaround: rad-chip blocks click events, so we trigger clicks from mouseup
      button.addEventListener('mouseup', (e) => {
        e.stopPropagation(); // Prevent rad-chip from interfering
        
        // Manually dispatch click event
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
          detail: 1
        });
        button.dispatchEvent(clickEvent);
      });
      
      // Listen for the click event (from our manual dispatch or natural clicks)
      button.addEventListener('click', (e) => {
        // Emit custom event that crosses shadow DOM boundary
        const customEvent = new CustomEvent('shadowButtonClick', {
          bubbles: true,
          composed: true,
          detail: { 
            button, 
            originalEvent: e,
            data: button.dataset
          }
        });
        button.dispatchEvent(customEvent);
      }, true);
    });

  }

  private cleanup() {
    // Remove managed elements
    this.managedElements.forEach(element => {
      if (element.classList.contains('shadow-dom-injected')) {
        element.remove();
      } else {
        // Reset inline styles for hidden elements
        element.style.display = '';
      }
    });
    this.managedElements.clear();

    // Restore removed elements if needed (optional)
    this.removedElements.clear();
  }
}

export interface ShadowDomConfig {
  /** The action to perform: 'remove', 'add', 'replace', 'hide', or 'removeClass' */
  action: 'remove' | 'add' | 'replace' | 'hide' | 'removeClass';
  
  /** CSS selector for the target element(s) in shadow DOM */
  selector: string;
  
  /** HTML content to add or replace (for 'add' and 'replace' actions, alternative to element) */
  html?: string;
  
  /** DOM element to add or replace (for 'add' and 'replace' actions, alternative to html) */
  element?: HTMLElement;
  
  /** Position for 'add' action: 'prepend', 'append', 'before', 'after' (default: 'append') */
  position?: 'prepend' | 'append' | 'before' | 'after';
  
  /** Class name to remove (required for 'removeClass' action) */
  className?: string;
}

